import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatLegacyFormField as MatFormField } from '@angular/material/legacy-form-field';
import { Observable } from 'rxjs';
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import { TreoAnimations } from 'tsui/@treo';
import { Account, FieldDef } from 'app/core/models/account.types';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { RelatedToType } from 'app/shared/enums';
import { Select, Store } from '@ngxs/store';
import { SearchResultHighlight } from '../../../shared/models/global-search';
import { GlobalSearchService } from 'app/services/global-search.service';
import { Dictionary } from 'app/shared/models';
import { plural } from 'pluralize';
import { AccountState } from '@state/account/state';
import { FeatureFlag } from '@core/enums';
import { ObjectsManagerState } from '@modules/settings/objects-manager/state/state';

@Component({
  selector: 'search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  encapsulation: ViewEncapsulation.None,
  exportAs: 'treoSearch',
  animations: TreoAnimations,
})
export class SearchComponent extends OnDestroyMixin implements OnInit {
  results: any[] = null;
  searchControl: UntypedFormControl;
  ui = { isLoading: false };
  private isNewCrmOn = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_CRM));

  private _appearance: 'basic' | 'bar';
  private _opened: boolean;
  private fieldDefs: FieldDef[] = [];

  @Select(ObjectsManagerState.getAllFields) private fieldDefs$: Observable<FieldDef[]>;

  @Input() debounce: number;
  @Input() account: Account;
  @Input() minLength: number;
  @Input() set appearance(value: 'basic' | 'bar') {
    // If the value is the same, return...
    if (this._appearance === value) {
      return;
    }

    // Make sure the search is closed, before
    // changing the appearance to prevent issues
    this.close();

    let appearanceClassName;

    // Remove the previous appearance class
    appearanceClassName = 'search-appearance-' + this.appearance;
    this.renderer2.removeClass(this.elementRef.nativeElement, appearanceClassName);

    // Store the appearance
    this._appearance = value;

    // Add the new appearance class
    appearanceClassName = 'search-appearance-' + this.appearance;
    this.renderer2.addClass(this.elementRef.nativeElement, appearanceClassName);
  }
  @Output() search: EventEmitter<any>;

  @ViewChild('searchInput') set searchInput(value: MatFormField) {
    // Return if the appearance is basic, since we don't want
    // basic search to be focused as soon as the page loads
    if (this.appearance === 'basic') {
      return;
    }

    // If the value exists, it means that the search input
    // is now in the DOM and we can focus on the input..
    if (value) {
      // Give Angular time to complete the change detection cycle
      setTimeout(() => {
        // Focus to the input element
        value._inputContainerRef.nativeElement.children[0].focus();
      });
    }
  }

  get appearance(): 'basic' | 'bar' {
    return this._appearance;
  }

  set opened(value: boolean) {
    // If the value is the same, return...
    if (this._opened === value) {
      return;
    }

    // Store the opened status
    this._opened = value;

    // If opened...
    if (value) {
      // Add opened class
      this.renderer2.addClass(this.elementRef.nativeElement, 'search-opened');
    } else {
      // Remove opened class
      this.renderer2.removeClass(this.elementRef.nativeElement, 'search-opened');
    }
  }

  get opened(): boolean {
    return this._opened;
  }

  constructor(
    private readonly elementRef: ElementRef,
    private readonly renderer2: Renderer2,
    private readonly globalSearchService: GlobalSearchService,
    private readonly store: Store,
  ) {
    super();
    this.appearance = 'basic';
    this.debounce = this.debounce || 500;
    this.minLength = this.minLength || 2;
    this.opened = false;
    this.results = null;
    this.searchControl = new UntypedFormControl();
  }

  ngOnInit(): void {
    this.fieldDefs$.pipe(untilComponentDestroyed(this)).subscribe((fieldDefs) => (this.fieldDefs = fieldDefs));

    this.searchControl.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        debounceTime(this.debounce),
        filter((query: string) => {
          if (!query?.length) {
            this.results = null;
          }

          return query?.length >= 2;
        }),
        tap(() => (this.ui = { isLoading: true })),
        switchMap((query) =>
          this.globalSearchService.search({
            query,
            accountId: Number(this.account.id),
          }),
        ),
        map((results) =>
          Object.keys(results)
            .reduce((prev, curr) => [...prev, ...results[curr].map((item) => ({ ...item, relatedToType: curr }))], [])
            .map((item) => ({
              ...item,
              link: this.jupiterLink(item),
              highlights: this.parseHightlights(item.search_highlights, item.relatedToType),
            })),
        ),
      )
      .subscribe((results) => {
        this.results = results || [];
        this.ui = { isLoading: false };
      });
  }

  selectOption(): void {
    this.close();
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.appearance === 'bar' && event.key === 'Escape') {
      this.close();
    }
  }

  open(): void {
    if (this.opened) {
      return;
    }

    this.opened = true;
  }

  close(): void {
    if (!this.opened) {
      return;
    }

    this.searchControl.setValue('');
    this.opened = false;
    this.results = null;
  }

  private parseHightlights(value: Dictionary<any>, relatedToType: RelatedToType): SearchResultHighlight[] {
    if (!value) {
      return [];
    }

    return Object.keys(value).reduce((prev, fieldName) => {
      const fieldDef = this.fieldDefs.find(
        ({ name, related_to_type }) => name === fieldName && related_to_type == relatedToType,
      );
      const label = fieldDef?.label ?? fieldName;

      return [...prev, { label, content: value[fieldName].replace(/<.*?>/g, '') }];
    }, []);
  }

  jupiterLink(item: any): string {
    const urlKey = plural(item.relatedToType).toLowerCase();

    if ([RelatedToType.Contact, RelatedToType.Company].includes(item.relatedToType)) {
      if (this.isNewCrmOn) {
        return `/space/void?return=/space/${urlKey}/0/${item.id}`;
      } else {
        return `/${urlKey}/${item.id}`;
      }
    } else if (item.relatedToType === RelatedToType.Project) {
      const [, pipelineId] = item.link.split('/').reverse();
      return `/projects/${pipelineId}/${item.id}`;
    } else if (item.relatedToType === RelatedToType.Property) {
      return `/space/void?return=/space/properties/0/${item.id}`;
    } else if (item.relatedToType === RelatedToType.PropertyTenant) {
      return `/space/void?return=/space/leases/0/${item.id}`;
    }

    return item.link;
  }
}
